import humanizeString from 'humanize-string';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Config, FieldStruct, ImmutableTree, Utils } from '@react-awesome-query-builder/core';
import { Controller, FieldPath, useFormContext } from 'react-hook-form';
import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { EVENT_NAMES_BY_SOURCES, EventName, EventSource, Feature } from '@loyalty/shared-common';
import { ErrorBoundary } from 'react-error-boundary';
import { ManagerIncomingWebhookAttributes } from '@loyalty/shared-common/manager-api-client';
import {
  QlfConfirmationDialog,
  QlfPlaceholder,
  colorPalette,
  useConfirmationDialog,
} from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { useTranslation } from 'react-i18next';
import {
  BaseConfigSettings,
  configureFieldsForEarningRules,
} from '../rules/components/query-builder/config/fields';
import { EMPTY_EARNING_RULES_CRITERIA } from './EarningRulesTable';
import { EarningRuleFormValue } from './form';
import { PeriodicityLimitationForm } from '../rules/components/periodicity-limitation-form';
import { RulesQueryBuilder, initialConfig } from '../rules/components';
import { SxStyles } from '../shared/utils/types';
import { excludedOperators } from '../rules/components/query-builder/config/settings';
import { filterBinaryWebhooks } from '../program-incoming-webhooks/incoming-webhook-utils';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useCustomKeys } from '../custom-keys/CustomKeysContext';
import { useEventSourceValues, useEventSourceValuesValue } from '../rules/rule-table-constants';
import { useFeatures } from '../features/FeaturesContext';
import { useIncomingWebhooks } from '../program-incoming-webhooks/IncomingWebhookContext';
import { useLevels } from '../levels/LevelsContext';
import { useSelectiveEffect } from '../shared/use-selective-effect';

const { checkTree, loadFromJsonLogic, _loadFromJsonLogic } = Utils;

const styles: SxStyles = {
  categoryField: {
    width: 'auto',
    minWidth: 200,
    marginRight: 1,
  },
  dropdownLabel: {
    margin: 0,
    marginRight: 0.8,
    marginBottom: 0.8,
  },
};

interface Props {
  className?: string;
}

function makeTree(jsonLogic: any, conf: Config): ImmutableTree {
  let newTree: ImmutableTree | undefined;
  const [tree, errors] = _loadFromJsonLogic(jsonLogic || EMPTY_EARNING_RULES_CRITERIA, conf);
  if (!errors?.length) {
    newTree = tree;
  } else {
    //dirty fix because loadFromJsonLogic silently drops criteria if it's missing in the config
    fixConfigForDeprecatedCriteria(conf, errors);
    newTree = loadFromJsonLogic(jsonLogic || EMPTY_EARNING_RULES_CRITERIA, conf);
  }
  if (!newTree) {
    throw new Error('Could not make tree based on jsonLogic');
  }
  return checkTree(newTree, conf);
}

function fixConfigForDeprecatedCriteria(conf: Config, errors: string[]) {
  errors.forEach(error => {
    if (
      error.startsWith('No config for field event.formattedCounters.') ||
      error.startsWith('No config for field event.formattedTags.')
    ) {
      const missingField = error.replace('No config for field event.', '');
      const [sectionName, criteriaName] = missingField.split('.');
      if (!(conf.fields.event as FieldStruct)['subfields'][sectionName]) {
        (conf.fields.event as FieldStruct)['subfields'][sectionName] = {
          label: sectionName,
          type: '!struct',
          subfields: [] as any,
        };
      }
      (conf.fields.event as any)['subfields'][sectionName]['subfields'][criteriaName] = {
        label: `${humanizeString(criteriaName)} (\u{26A0} MISSING \u{26A0})`,
        excludeOperators: excludedOperators,
        valueSource: ['value'],
        type: sectionName === 'formattedCounters' ? 'number' : 'text',
      };
    }
  });
}

function makeConfig(
  settings: BaseConfigSettings,
  readOnly = false,
  options?: { customKeys?: CustomKeyAttributes[]; webhooks?: ManagerIncomingWebhookAttributes[] },
): Config {
  return {
    ...initialConfig(settings.t, readOnly),
    fields: configureFieldsForEarningRules(settings, options),
  };
}

function areCriteriaEmpty(criteria: unknown): boolean {
  return !criteria || isEqual(criteria, EMPTY_EARNING_RULES_CRITERIA);
}

export function EarningRulePanelCriteria({ className }: Props): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { currencies } = useCurrencies();
  const { levels } = useLevels();
  const { features } = useFeatures();
  const { write: earningRuleWrite } = features[Feature.EARNING_RULE];
  const { webhooks } = useIncomingWebhooks();

  const binaryWebhooks = filterBinaryWebhooks(webhooks);

  const {
    watch,
    setValue,
    getValues,
    control,
    register,
    formState: { errors },
  } = useFormContext<EarningRuleFormValue>();

  const [readyToLoadQB, setReadyToLoadQB] = useState(false);

  const criteriaFieldName = 'criteria';
  const eventSourceValue = watch('eventSource');
  const eventNameValue = watch('eventName');

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();

  const sourceTranslationsValues = useEventSourceValues();
  const sourceTranslationNames = useEventSourceValuesValue();

  useEffect(() => {
    setReadyToLoadQB(true);
  }, []);

  const settings = useMemo<BaseConfigSettings>(
    () => ({
      eventSource: eventSourceValue,
      eventName: eventNameValue,
      locale: i18n.language,
      currencies,
      levels,
      t,
    }),
    [eventSourceValue, eventNameValue, i18n.language, currencies, levels, t],
  );

  const { customKeys } = useCustomKeys();
  const [config, setConfig] = useState<Config>(
    makeConfig(settings, !earningRuleWrite, { customKeys, webhooks }),
  );

  const jsonLogic = getValues().criteria;
  const [tree, setTree] = useState<ImmutableTree>(makeTree(jsonLogic, config));

  const resetCriteria = useCallback(() => {
    setTree(makeTree(EMPTY_EARNING_RULES_CRITERIA, config));
  }, [config]);

  useEffect(() => {
    register(criteriaFieldName);
  }, [register]);

  const showCategoryChangeDialog = useCallback(
    (onConfirm: () => void) =>
      setConfirmationDialogState({
        ...confirmationDialogState,
        open: true,
        content: t('earning-rules.confirm.reset.message'),
        confirmButtonText: t('earning-rules.confirm.reset.button'),
        confirmButtonIcon: faCheck,
        onConfirm,
      }),
    [confirmationDialogState, setConfirmationDialogState, t],
  );

  const handleCategoriesChange = useCallback(
    (name: FieldPath<EarningRuleFormValue>, value: EventName) => {
      const criteria = getValues().criteria;

      const makeChange = () => {
        const newConfig = makeConfig(
          { ...settings, [name.split('.').pop() as string]: value },
          !earningRuleWrite,
          { customKeys, webhooks },
        );
        const newTree = makeTree(null, newConfig);

        setReadyToLoadQB(false);
        setValue(criteriaFieldName, EMPTY_EARNING_RULES_CRITERIA, { shouldDirty: true });
        setConfig(newConfig);
        setTree(newTree);
        setValue(name, value, { shouldDirty: true });
        if (name === 'eventSource') {
          setValue('eventName', eventNamesBySource[value][0]?.value as EventName, {
            shouldDirty: true,
          });
        }
        setTimeout(() => {
          setReadyToLoadQB(true);
        }, 0);
        setConfirmationDialogState({
          ...confirmationDialogState,
          open: false,
        });
      };

      if (!areCriteriaEmpty(criteria)) {
        showCategoryChangeDialog(makeChange);
        return false;
      } else {
        setValue(name, value, { shouldDirty: true });
        if (name === 'eventSource') {
          setValue('eventName', eventNamesBySource[value][0]?.value as EventName, {
            shouldDirty: true,
          });
        }
      }
    },
    [
      getValues,
      settings,
      earningRuleWrite,
      customKeys,
      setValue,
      setConfirmationDialogState,
      confirmationDialogState,
      showCategoryChangeDialog,
    ],
  );

  const handleQueryBuilderChange = useCallback(
    (immutableTree: ImmutableTree, conf: Config, newValue: Record<string, unknown>) => {
      setTree(immutableTree);
      setConfig(conf);
      setValue(criteriaFieldName, newValue, { shouldDirty: true });
    },
    [setValue],
  );

  useSelectiveEffect(
    () => {
      const newConfig = makeConfig(settings, !earningRuleWrite, { customKeys, webhooks });
      setConfig(newConfig);
      setTree(makeTree(jsonLogic, newConfig));
    },
    [customKeys, webhooks, earningRuleWrite, jsonLogic, settings],
    [settings],
  );

  const eventSources = Object.keys(EVENT_NAMES_BY_SOURCES);

  const eventNamesBySource: { [key: string]: { label: string; value: string }[] } = {};
  Object.entries(EVENT_NAMES_BY_SOURCES).forEach(([key, value]) => {
    eventNamesBySource[key] = value.map(name => ({ label: name, value: name }));
  });

  eventNamesBySource[EventSource.BINARY].push(
    ...binaryWebhooks.map(webhook => ({
      label: webhook.name,
      value: `webhook_${webhook.id}`,
    })),
  );

  return (
    <div className={className}>
      <div>
        <Box component="div" sx={{ color: colorPalette.grey.dark, marginBottom: 2 }}>
          {t('earning-rules.properties.define-event')}
        </Box>
        <Box component="div" sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography variant="h3" sx={styles.dropdownLabel}>
            {t('earning-rules.properties.eventSource')}
          </Typography>
          <Controller
            control={control}
            name="eventSource"
            render={({ field, fieldState: { error } }) => (
              <FormControl sx={styles.categoryField}>
                <TextField
                  disabled={!earningRuleWrite}
                  {...field}
                  select
                  onChange={event =>
                    handleCategoriesChange(field.name, event.target.value as EventName)
                  }
                  error={!!error}
                  helperText={error?.message}
                  id="source-id"
                >
                  {eventSources.map(value => (
                    <MenuItem key={value} value={value} id={`source_${value}`}>
                      <>{(sourceTranslationsValues as Record<string, string>)[value]}</>
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}
          />
          <Typography variant="h3" sx={styles.dropdownLabel}>
            {t('earning-rules.properties.eventName')}
          </Typography>
          <Controller
            control={control}
            name="eventName"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <FormControl sx={styles.categoryField}>
                <TextField
                  {...field}
                  disabled={!earningRuleWrite}
                  select
                  inputRef={ref}
                  onChange={event =>
                    handleCategoriesChange(field.name, event.target.value as EventName)
                  }
                  error={!!error}
                  helperText={error?.message}
                  id="action-id"
                >
                  {eventNamesBySource[eventSourceValue].map(eventName => (
                    <MenuItem
                      key={eventName.value}
                      value={eventName.value}
                      id={`action_${eventName.value}`}
                    >
                      <>
                        {(sourceTranslationNames as Record<string, string>)[eventName.value] ||
                          eventName.label}
                      </>
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}
          />
        </Box>
      </div>
      <div>
        {!readyToLoadQB ? (
          <Skeleton />
        ) : (
          <ErrorBoundary
            FallbackComponent={({ resetErrorBoundary }) => (
              <QlfPlaceholder
                primaryText={t('error.title')}
                secondaryText={t('error.criteria.description')}
                actions={
                  <Button
                    onClick={() => {
                      resetCriteria();
                      resetErrorBoundary();
                    }}
                  >
                    {t('error.criteria.reset')}
                  </Button>
                }
              />
            )}
            resetKeys={[tree, config]}
          >
            <RulesQueryBuilder
              // errors[criteriaFieldName]?.message is not considered string|undefined because of typing issue
              error={
                errors[criteriaFieldName]?.message
                  ? `${errors[criteriaFieldName]?.message}`
                  : undefined
              }
              tree={tree}
              onChange={handleQueryBuilderChange}
              config={config}
            />
          </ErrorBoundary>
        )}
      </div>
      <div>
        <Typography variant="h3">{t('earning-rules.properties.options')}</Typography>

        <Box mb={1}>
          <Controller
            control={control}
            name="stopTriggering"
            render={({ field: { ref, ...field } }) => (
              <FormControlLabel
                control={<Switch {...field} disabled={!earningRuleWrite} checked={field.value} />}
                label={t('earning-rules.properties.stop-triggering') as string}
              />
            )}
          />
        </Box>

        <PeriodicityLimitationForm disabled={!earningRuleWrite} />
      </div>

      <QlfConfirmationDialog {...confirmationDialogState} />
    </div>
  );
}
